import { useEffect, useState } from "react";
import { BadgeCheck, Loader2, Mail, Phone, Search } from "lucide-react";
import useApiMethods from "../../../lib/useApiMethods";
import { RESEARCH_SUMMARY_URL } from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";

/**
 * The row under the lead's name: company research state + fit score, and
 * whether the main phone and email are verified (contactVerification.js).
 *
 * The research chip refetches when the Company Research tab reports a change
 * (RESEARCH_CHANGED_EVENT), so it turns "Completed" without a page reload.
 */
export const RESEARCH_CHANGED_EVENT = "research:changed";

const chip = "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium";
const tone = {
    ok: "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300",
    warn: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300",
    busy: "border-brand-200 bg-brand-50 text-primary dark:border-brand-800 dark:bg-brand-900/20",
    muted: "border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-600 dark:bg-gray-700/40 dark:text-gray-300",
};

/** Primary contact first, then any contact that has the value; else the lead's own number. */
export function mainContact(contacts = [], kind, leadPhone) {
    const key = kind === "email" ? "email" : "contact_number";
    const sorted = [...contacts].sort((a, b) => (b.is_primary_contact ? 1 : 0) - (a.is_primary_contact ? 1 : 0));
    const c = sorted.find(x => x[key]);
    if (c) return { contact: c, value: c[key] };
    return kind === "phone" && leadPhone ? { contact: null, value: leadPhone } : null;
}

function ResearchChip({ job, enabled, onOpen }) {
    let cls = tone.muted, icon = <Search className="h-3 w-3" aria-hidden="true" />, text = "Not researched";
    if (job) {
        if (["queued", "running"].includes(job.status)) {
            cls = tone.busy;
            icon = <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />;
            text = job.status === "queued" ? "Research queued" : `Researching: ${job.stage_label || "starting"}`;
        } else if (["completed", "partial"].includes(job.status)) {
            cls = tone.ok;
            icon = <BadgeCheck className="h-3 w-3" aria-hidden="true" />;
            text = `AI research ${job.status === "partial" ? "partial" : "done"}`
                + (job.fit_score != null ? ` · Fit ${job.fit_score}` : "")
                + (job.pending_reviews ? ` · ${job.pending_reviews} to review` : "");
        } else if (job.status === "needs_review") {
            cls = tone.warn;
            text = "Research: pick website";
        } else if (job.status === "failed") {
            cls = tone.warn;
            text = "Research failed";
        }
    } else if (!enabled) {
        return null;
    }
    const title = job?.finished_at ? `Researched ${job.finished_at}${job.website ? ` · ${job.website}` : ""}` : "Open Company Research";
    return (
        <button type="button" onClick={onOpen} title={title} className={cn(chip, cls, "hover:opacity-80")}>
            {icon}{text}
        </button>
    );
}

function ContactChip({ kind, main, flags }) {
    if (!main) return null;
    const Icon = kind === "email" ? Mail : Phone;
    const row = main.contact ? flags[main.contact.id] : null;
    const verified = !!row?.[`${kind}_verified`];
    const how = row?.[`${kind}_verification`];
    const title = verified
        ? `Verified${how?.via === "manual" ? ` by ${how.by || "staff"}` : how?.via ? ` through the ${how.via} link` : ""}`
        : "Not verified yet";
    return (
        <span className={cn(chip, verified ? tone.ok : tone.muted, "max-w-full")} title={title}>
            <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">{main.value}</span>
            <span className={cn("shrink-0", verified ? "" : "text-gray-400")}>
                {verified ? <><BadgeCheck className="inline h-3 w-3" aria-hidden="true" /> Verified</> : "· Not verified"}
            </span>
        </span>
    );
}

export default function LeadStatusChips({ customerId, contacts, leadPhone, flags = {}, onOpenResearch,
                                         showResearch = true }) {
    const { fetch } = useApiMethods();
    const [research, setResearch] = useState(null);

    useEffect(() => {
        if (!customerId || !showResearch) return undefined;
        let live = true;
        const load = () => fetch(RESEARCH_SUMMARY_URL(customerId)).then(res => {
            if (live && res?.status === "success") setResearch(res.data);
        });
        load();
        const onChange = (e) => { if (String(e.detail?.customerId) === String(customerId)) load(); };
        window.addEventListener(RESEARCH_CHANGED_EVENT, onChange);
        return () => { live = false; window.removeEventListener(RESEARCH_CHANGED_EVENT, onChange); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, showResearch]);

    return (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5" aria-label="Lead status">
            {showResearch && research && <ResearchChip job={research.job} enabled={research.enabled} onOpen={onOpenResearch} />}
            <ContactChip kind="phone" main={mainContact(contacts, "phone", leadPhone)} flags={flags} />
            <ContactChip kind="email" main={mainContact(contacts, "email")} flags={flags} />
        </div>
    );
}
